import React, { useEffect, useState } from 'react';
import { useSetDropdownAndRole } from 'hooks/useSetDropdownAndRole';
import { useTranslation } from 'react-i18next';
import { useGetAllTasksMutation } from 'redux/services/project';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getTaskType } from './utils';
import { Loader } from 'components/common/Loader';
import { TaskList } from './TaskList';
import { Button, Pagination } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { TASK_TYPES } from './constants';
import './Tasks.scss';
import { FormSelect } from 'components/common/FormSelect';
import { useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import toast from 'react-hot-toast';

const Tasks = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<any>(null);

  const taskType = getTaskType(location.pathname);

  const [
    getAllTasks,
    { data: allTasks, isLoading: allTasksLoading, isError, reset },
  ] = useGetAllTasksMutation();
  const projects = useSelector((state: RootStates) => state.project.projects);

  useSetDropdownAndRole();

  useEffect(() => {
    if (isError) {
      toast.error('Error loading tasks. Please try again later');
      reset();
    }
  }, [isError, reset]);

  useEffect(() => {
    getAllTasks({ taskType: taskType || '', projectId: projectId || '' });
  }, [getAllTasks, projectId, taskType]);

  useEffect(() => {
    if (projects.length && projectId) {
      const p = projects.find((p) => p.projectInfo.id === projectId);

      if (p) {
        setSelectedProject({
          ...p,
          id: p.projectInfo.projectId,
          name: p.projectInfo.projectName,
        });
      }
    }
  }, [projectId, projects]);

  const handleBack = () => navigate(-1);

  const handlePaginationChange = (
    _: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    if (Number(allTasks?.pagination?.page) !== page) {
      getAllTasks({
        taskType: taskType || '',
        projectId: projectId || '',
        page,
      });
    }
  };

  const handleSelectProject = (project: any) => {
    if (taskType) {
      navigate(
        `/project/${project.projectInfo.id}/${taskType.toLowerCase()}/tasks`,
        { replace: true },
      );
    } else {
      navigate(`/project/${project.projectInfo.id}/tasks`, { replace: true });
    }
  };

  const handleSelectTaskType = (task: any) => {
    navigate(`/project/${projectId}/${task.type.toLowerCase()}/tasks`, {
      replace: true,
    });
  };

  return (
    <div className="tasks">
      <h4 className="page-title">{t('header_tasks')}</h4>
      <div className="d-flex setup-container flex-column">
        {allTasksLoading ? <Loader /> : null}
        <div className="dropdown-container d-flex gap-3 gap-md-2 mb-3 w-100 justify-content-start flex-column flex-md-row">
          <FormSelect
            placeholder="Select Project"
            selectedItem={selectedProject}
            items={projects?.map((p) => ({
              ...p,
              id: p.projectInfo.projectId,
              name: p.projectInfo.projectName,
            }))}
            uniqueId="id"
            onSelect={handleSelectProject}
          />
          <FormSelect
            placeholder="Select Task Type"
            items={TASK_TYPES}
            disabled={!projectId}
            selectedItem={TASK_TYPES.find((t) => t.type === taskType)}
            uniqueId="name"
            onSelect={handleSelectTaskType}
          />
        </div>
        {!isError && <TaskList tasks={allTasks?.data ?? []} />}
      </div>
      <div className="d-flex justify-content-between my-1">
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBack}
        >
          {t('btn_back')}
        </Button>
        {allTasks?.pagination?.totalPage > 1 && (
          <Pagination
            className="align-self-end"
            count={allTasks?.pagination?.totalPage ?? 0}
            page={Number(allTasks?.pagination?.page ?? 1)}
            color="primary"
            shape="rounded"
            onChange={handlePaginationChange}
          />
        )}
      </div>
    </div>
  );
};

export default Tasks;
